import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Popconfirm, 
  message
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getUserList, deleteUser } from '../../api/user';
import UserModal from './components/UserModal';
import './index.css';

const UserManagement = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 获取用户列表
  const fetchUserList = async (pageNum = 1, pageSize = 10) => {
    console.log('📊 请求用户列表 - 页码:', pageNum, '每页数量:', pageSize);
    setLoading(true);
    
    // 先清空数据，避免残留
    setDataSource([]);
    
    try {
      const result = await getUserList({ pageNum, pageSize });
      console.log('📊 后端返回数据:', result);
      
      if (result.code === 200 && result.data) {
        // 数据在 data.userList 中
        const userList = result.data.userList || [];
        console.log('📊 用户列表长度:', userList.length);
        console.log('📊 分页信息:', {
          pageNum: result.data.pageNum,
          pageSize: result.data.pageSize,
          total: result.data.total
        });
        
        // 为每条数据添加唯一的key，避免重复
        const dataWithKeys = userList.map((item, index) => ({
          ...item,
          _uniqueKey: `${result.data.pageNum}-${index}-${item.phone}`
        }));
        
        setDataSource(dataWithKeys);
        setPagination({
          current: result.data.pageNum || pageNum,
          pageSize: result.data.pageSize || pageSize,
          total: result.data.total || 0
        });
      } else {
        message.error(result.msg || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error(error.message || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList();
  }, []);

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    fetchUserList(pagination.current, pagination.pageSize);
  };

  // 打开新增/编辑弹窗
  const handleOpenModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setEditingRecord(null);
    setModalVisible(false);
  };

  // 保存成功后刷新列表
  const handleSaveSuccess = () => {
    handleCloseModal();
    fetchUserList(pagination.current, pagination.pageSize);
  };

  // 删除用户
  const handleDelete = async (phone) => {
    try {
      await deleteUser(phone);
      message.success('删除成功');
      fetchUserList(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '账号',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '注册时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (time) => time || '-'
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        const isAdmin = record.name === 'admin'; // 判断是否为admin用户
        
        return (
          <Space size="middle">
            <Button 
              type="link" 
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除这个用户吗？"
              onConfirm={() => handleDelete(record.phone)}
              okText="确定"
              cancelText="取消"
              disabled={isAdmin}
            >
              <Button 
                type="link" 
                danger
                icon={<DeleteOutlined />}
                disabled={isAdmin}
                title={isAdmin ? 'admin用户不能删除' : ''}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="user-management">
      <div className="list-header">
        <h2>用户管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          创建用户
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="_uniqueKey"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
      />
      <UserModal
        visible={modalVisible}
        record={editingRecord}
        onCancel={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
};

export default UserManagement;

